/**
 * UI catalog for conversation categories (single frontend source).
 * Keep aligned with: app/modules/openAi/conversation_category_catalog.py
 * API mirror: GET /chats/meta/message-categories/
 *
 * Where: Inbox filters, chips, and category pickers (`MessageList`, `ConversationView`).
 * Uses: pure data + helper functions (`getCategoryLabel`, colors); no network I/O in this file.
 * Behavior: must stay in sync with backend enum definitions noted above.
 * Invariants: keys/labels must match `app/modules/openAi/conversation_category_catalog.py` and `/chats/meta/message-categories/`.
 */

export type ConversationCategoryKey =
  | 'connection_accepted'
  | 'awaiting_reply'
  | 'engaged'
  | 'meeting_requested'
  | 'meeting_accepted'
  | 'meeting_scheduled'
  | 'referral_in_progress'
  | 'follow_up_later'
  | 'not_interested'
  | 'not_a_match'
  | 'closed';

export type CategorySource = 'static' | 'ai' | 'derived';

export interface ConversationCategoryUiEntry {
  key: ConversationCategoryKey;
  label: string;
  color: string;
  source: CategorySource;
  description: string;
}

export const CONVERSATION_CATEGORY_UI_CATALOG: ConversationCategoryUiEntry[] = [
  {
    key: 'connection_accepted',
    label: 'Connection Accepted',
    color: '#4CAF50',
    source: 'static',
    description: 'Prospect accepted our connection request.',
  },
  {
    key: 'awaiting_reply',
    label: 'Awaiting Reply',
    color: '#FF9800',
    source: 'derived',
    description: 'Connection accepted but prospect has not replied yet.',
  },
  {
    key: 'engaged',
    label: 'Engaged',
    color: '#8BC34A',
    source: 'ai',
    description: 'Prospect shows positive willingness to continue the conversation.',
  },
  {
    key: 'meeting_requested',
    label: 'Meeting Requested',
    color: '#03A9F4',
    source: 'ai',
    description: 'We asked the prospect for a meeting in the conversation.',
  },
  {
    key: 'meeting_accepted',
    label: 'Meeting Accepted',
    color: '#2196F3',
    source: 'ai',
    description: 'Prospect explicitly agreed to have a meeting with us.',
  },
  {
    key: 'meeting_scheduled',
    label: 'Meeting Scheduled',
    color: '#1565C0',
    source: 'static',
    description: 'Meeting scheduling email has been sent to the prospect.',
  },
  {
    key: 'referral_in_progress',
    label: 'Referral in Progress',
    color: '#9C27B0',
    source: 'ai',
    description: 'Prospect referred or suggested another person to talk to.',
  },
  {
    key: 'follow_up_later',
    label: 'Follow Up Later',
    color: '#607D8B',
    source: 'ai',
    description: 'Prospect asked to reconnect or follow up at a later time.',
  },
  {
    key: 'not_interested',
    label: 'Not Interested',
    color: '#F44336',
    source: 'ai',
    description: 'Prospect expressed disinterest in the offer or topic.',
  },
  {
    key: 'not_a_match',
    label: 'Not a Match',
    color: '#FF5722',
    source: 'ai',
    description: 'Prospect is not the right person or not a fit for this topic.',
  },
  {
    key: 'closed',
    label: 'Closed',
    color: '#795548',
    source: 'ai',
    description: 'Conversation was closed by us.',
  },
];

const byKey: Record<ConversationCategoryKey, ConversationCategoryUiEntry> =
  {} as Record<ConversationCategoryKey, ConversationCategoryUiEntry>;
for (const e of CONVERSATION_CATEGORY_UI_CATALOG) {
  byKey[e.key] = e;
}

export function getCategoryUi(
  key: string,
): ConversationCategoryUiEntry | undefined {
  return byKey[key as ConversationCategoryKey];
}

export function getCategoryLabel(key: string): string {
  return byKey[key as ConversationCategoryKey]?.label ?? key.replace(/_/g, ' ');
}

export function getCategoryColor(key: string): string {
  return byKey[key as ConversationCategoryKey]?.color ?? '#757575';
}

/** Inbox filter chips / EntityMultiSelect options */
export const CATEGORY_FILTER_OPTIONS = CONVERSATION_CATEGORY_UI_CATALOG.map(
  (e) => ({
    value: e.key,
    label: e.label,
    color: e.color,
  }),
);
